import { skipToken, useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader } from 'lucide-react'
import { Suspense, useEffect, useRef, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod/v4'
import { PasswordConfirmField } from '@/components/sv/password-confirm-field'
import { ToasterContent } from '@/components/sv/toaster-content'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import { gql } from '@/lib/graphql/gql'
import {
  useIdCompany,
  usePasswordToken,
  useRBAC,
  useSuspenseQuery,
} from '@/lib/hooks'
import { passwordFieldSchema } from '@/lib/password-field-schema'
import {
  canCompanyDeveloperManageCapabilities,
  isCompanyDeveloper,
} from '@/lib/rbac'
import { RemoveUserFromCompanyDialogFallback } from '@/routes/user-management/remove-user-from-company-dialog/fallback'
import type {
  CompanyUserRemovalContextQuery,
  CompanyUserRemovalContextQueryVariables,
  RemoveUsersFromCompanyMutation,
  RemoveUsersFromCompanyMutationVariables,
} from '@/routes/user-management/remove-user-from-company-dialog/index.types.gen'

const QUERY_COMPANY_USER = gql`
  query CompanyUserRemovalContext($idCompany: String!, $idUser: String!) {
    company(id: $idCompany) {
      id
      userByIdUser(idUser: $idUser) {
        id
        user {
          id
          email
          fullName
        }
        capabilities {
          id
          idApp
          idDatabase
          idStaticSite
          idObjectStorage
          idLoadBalancer
          idProject
          idRole
          type
        }
      }
    }
  }
`

const REMOVE_USERS_FROM_COMPANY = gql`
  mutation RemoveUsersFromCompany(
    $idCompany: String!
    $idUsers: [String!]!
    $passwordToken: String
  ) {
    removeUsersFromCompany(
      idCompany: $idCompany
      idUsers: $idUsers
      passwordToken: $passwordToken
    )
  }
`

const schema = z.object({
  password: passwordFieldSchema,
})

type FormValues = z.infer<typeof schema>

type Props = {
  isOpen: boolean
  onClose: () => void
}

const DialogContent = ({ isOpen, onClose }: Props) => {
  const { idUser = '' } = useParams<{ idUser: string }>()
  const { idCompany } = useIdCompany()
  const { rolesInCompany } = useRBAC()
  const { obtainPasswordToken } = usePasswordToken()
  const [isPending, startTransition] = useTransition()
  const closeReasonRef = useRef<'blocked' | 'missing' | null>(null)
  const isCompanyDeveloperViewer = isCompanyDeveloper(rolesInCompany)

  const [removeUser] = useMutation<
    RemoveUsersFromCompanyMutation,
    RemoveUsersFromCompanyMutationVariables
  >(REMOVE_USERS_FROM_COMPANY)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      password: '',
    },
  })

  const { data } = useSuspenseQuery<
    CompanyUserRemovalContextQuery,
    CompanyUserRemovalContextQueryVariables
  >(
    QUERY_COMPANY_USER,
    isOpen
      ? { variables: { idCompany, idUser }, fetchPolicy: 'no-cache' }
      : skipToken
  )

  const companyUser = data?.company?.userByIdUser
  const isBlockedForCompanyDeveloper =
    isCompanyDeveloperViewer &&
    !canCompanyDeveloperManageCapabilities(companyUser?.capabilities ?? [])
  const closeReason = !isOpen
    ? null
    : !idUser || !companyUser
      ? 'missing'
      : isBlockedForCompanyDeveloper
        ? 'blocked'
        : null
  const userLabel =
    companyUser?.user?.fullName || companyUser?.user?.email || 'this user'

  useEffect(() => {
    if (!closeReason) {
      closeReasonRef.current = null
      return
    }

    if (closeReasonRef.current === closeReason) {
      return
    }

    closeReasonRef.current = closeReason
    toast(
      <ToasterContent variant="error">
        {closeReason === 'blocked'
          ? 'Company developers can only remove project-level or service-level users'
          : 'User not found'}
      </ToasterContent>,
      { position: 'top-right' }
    )
    onClose()
  }, [closeReason, onClose])

  useEffect(() => {
    if (!closeReason) {
      form.setFocus('password')
    }
  }, [closeReason, form])

  const onSubmit = ({ password }: FormValues) => {
    if (isPending) {
      return
    }

    startTransition(async () => {
      const result = await obtainPasswordToken(password)
      if (result.error) {
        form.setError('password', { message: result.error })
        return
      }

      try {
        await removeUser({
          variables: {
            idCompany,
            idUsers: [idUser],
            passwordToken: result.passwordToken,
          },
        })
        toast(
          <ToasterContent variant="success">
            User removed successfully from the company
          </ToasterContent>,
          { position: 'top-right' }
        )
        onClose()
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to remove user from the company
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  if (closeReason) {
    return null
  }

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>Remove user</AlertDialogTitle>
        <AlertDialogDescription>
          Are you sure you want to remove <strong>{userLabel}</strong> from the
          company?
        </AlertDialogDescription>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-2 mt-2"
          >
            <PasswordConfirmField
              description="Enter your Sevalla password to remove this user."
              isDisabled={isPending}
            />
            <Button type="submit" hidden />
          </form>
        </Form>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={isPending} onClick={onClose}>
          Cancel
        </AlertDialogCancel>
        <AlertDialogAction
          disabled={isPending}
          onClick={(event) => {
            event.preventDefault()
            form.handleSubmit(onSubmit)()
          }}
        >
          {isPending ? <Loader className="animate-spin" /> : null}
          Continue
        </AlertDialogAction>
      </AlertDialogFooter>
    </>
  )
}

export const RemoveUserFromCompanyDialog = ({ isOpen, onClose }: Props) => {
  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <Suspense fallback={<RemoveUserFromCompanyDialogFallback />}>
          <DialogContent isOpen={isOpen} onClose={onClose} />
        </Suspense>
      </AlertDialogContent>
    </AlertDialog>
  )
}
